"use client";

import { useResolveHref } from "@/lib/links";
import { useEffect, useRef, useState } from "react";
import { ScrollTrigger, useGSAP } from "@/lib/gsap";
import { useLenis } from "@/hooks/useLenis";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/Button";
import { ChevronDown, Close, Menu as MenuIcon } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { MegaMenu } from "./MegaMenu";
import { MobileMenu } from "./MobileMenu";
import { LEFT_LINKS, MENUS, RIGHT_LINKS, type MenuId } from "./nav-data";
import { LINKS } from "@/content/quikit";

type Theme = "light" | "dark";

/**
 * Thin, fixed bar. Whenever a dark panel ([data-nav-theme="dark"]) slides beneath it, the
 * glass tint, text and CTA invert over 500ms — the bar adapts instead of fighting the page.
 */
export function Navbar() {
  const r = useResolveHref();
  const header = useRef<HTMLElement>(null);
  const [theme, setTheme] = useState<Theme>("light");
  const [scrolled, setScrolled] = useState(false);
  const [openId, setOpenId] = useState<MenuId | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const openRef = useRef<MenuId | null>(null);

  useEffect(() => {
    openRef.current = openId;
  }, [openId]);

  // Scrolling away dismisses an open dropdown, like a native menu would.
  const lenis = useLenis(() => {
    if (openRef.current) setOpenId(null);
  });

  useGSAP(
    () => {
      const navMid = () => (header.current?.offsetHeight ?? 60) / 2;
      const active = new Set<Element>();
      document.querySelectorAll<HTMLElement>('[data-nav-theme="dark"]').forEach((el) => {
        ScrollTrigger.create({
          trigger: el,
          start: () => `top ${navMid()}px`,
          end: () => `bottom ${navMid()}px`,
          onToggle: (self) => {
            if (self.isActive) active.add(el);
            else active.delete(el);
            setTheme(active.size ? "dark" : "light");
          },
        });
      });
      ScrollTrigger.create({ start: 6, end: "max", onToggle: (self) => setScrolled(self.isActive) });
    },
    { scope: header },
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenId(null);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!lenis) return;
    if (mobileOpen) lenis.stop();
    else if (document.documentElement.dataset.introDone) lenis.start();
  }, [mobileOpen, lenis]);

  const openMenu = (id: MenuId) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpenId(id), openRef.current ? 0 : 70);
  };
  const scheduleClose = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpenId(null), 170);
  };
  const cancelClose = () => window.clearTimeout(timer.current);

  const dark = theme === "dark" && !mobileOpen;
  const linkBase =
    "flex h-8 items-center gap-1 rounded-full px-3 text-[14px] font-medium tracking-[-0.01em] transition-[background-color,color] duration-300 ease-[var(--ease-premium)]";
  const linkTone = dark
    ? "text-white/72 hover:bg-white/10 hover:text-white"
    : "text-ink/75 hover:bg-ink/[0.05] hover:text-ink";

  return (
    <header
      ref={header}
      data-theme={dark ? "dark" : "light"}
      onMouseLeave={scheduleClose}
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-colors duration-500 ease-[var(--ease-premium)]",
        dark ? "text-white" : "text-ink",
      )}
    >
      <div
        aria-hidden="true"
        className={cn(
          "absolute inset-0 backdrop-blur-xl backdrop-saturate-150 transition-[background-color,box-shadow] duration-500 ease-[var(--ease-premium)]",
          dark
            ? "bg-[rgba(12,12,13,0.58)] shadow-[0_1px_0_rgba(255,255,255,0.06)]"
            : cn("bg-white/80", (scrolled || mobileOpen) && "shadow-[0_1px_0_rgba(10,10,11,0.06)]"),
        )}
      />

      <nav aria-label="Main" className="relative flex h-[var(--nav-h)] items-center justify-between px-[var(--gutter)]">
        <div className="flex items-center">
          <button
            type="button"
            data-nav-item=""
            data-intro=""
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            className={cn("-ml-2 grid size-10 place-items-center rounded-full lg:hidden", linkTone)}
          >
            {mobileOpen ? <Close className="size-5" /> : <MenuIcon className="size-5" />}
          </button>

          <ul className="-ml-3 hidden items-center gap-0.5 lg:flex">
            {MENUS.map((menu) => {
              const isOpen = openId === menu.id;
              const isCurrent = menu.id === "products";
              return (
                <li key={menu.id} data-nav-item="" data-intro="">
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    aria-current={isCurrent ? "page" : undefined}
                    onMouseEnter={() => openMenu(menu.id)}
                    onClick={() => setOpenId(isOpen ? null : menu.id)}
                    className={cn(
                      linkBase,
                      linkTone,
                      isOpen && !dark && "bg-ink/[0.05] text-ink",
                      isCurrent && dark && "bg-white text-ink hover:bg-white hover:text-ink",
                    )}
                  >
                    {menu.label}
                    <ChevronDown
                      className={cn(
                        "size-3 transition-transform duration-300 ease-[var(--ease-premium)]",
                        isOpen && "rotate-180",
                      )}
                    />
                  </button>
                </li>
              );
            })}
            {LEFT_LINKS.map((link) => (
              <li key={link.label} data-nav-item="" data-intro="">
                <a href={r(link.href)} onMouseEnter={scheduleClose} className={cn(linkBase, linkTone)}>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* The Quikit wordmark on every page, product pages included: a product's own mark
            now sits above the eyebrow in its hero, where it introduces the page rather than
            replacing the site's identity in the bar. */}
        <a
          href={r("#top")}
          aria-label="Quikit home"
          data-nav-logo=""
          data-intro=""
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          <Logo tone={dark ? "light" : "dark"} className="w-[104px] md:w-[118px]" />
        </a>

        <div className="flex items-center gap-1">
          <ul className="hidden items-center gap-0.5 lg:flex">
            {RIGHT_LINKS.map((link) => (
              <li key={link.label} data-nav-item="" data-intro="">
                <a href={r(link.href)} className={cn(linkBase, linkTone)}>
                  {link.label}
                </a>
              </li>
            ))}
            <li data-nav-item="" data-intro="">
              <a href={LINKS.login} className={cn(linkBase, linkTone)}>
                Log in
              </a>
            </li>
          </ul>
          <div data-nav-item="" data-intro="" className="ml-1">
            <Button size="sm" variant="mint" href={LINKS.register}>
              Start free
            </Button>
          </div>
        </div>
      </nav>

      <MegaMenu
        menu={MENUS.find((m) => m.id === openId) ?? null}
        onEnter={cancelClose}
        onLeave={scheduleClose}
        onNavigate={() => setOpenId(null)}
      />
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
}
